<template>
  <clinician-app-layout>
    <template #header>
      <inertia-link
        :href="route('clinician.shared-with-me')"
        class="lg:absolute relative md:left-7 flex items-center text-vice-teal-500 text-xs"
      >
        <ChevronLeftIcon class="w-4 h-4 mr-1" />
        <span class="flex-none">Shared With Me</span>
      </inertia-link>
      <div class="py-5 2xl:pt-0 border-gray-200">
        <h2 class="text-3xl max-w-3xl text-vice-teal-900 font-semibold leading-tight">
          {{ snapshot.session_name }}
        </h2>
        <p class="text-sm text-gray-600 mt-2">
          {{ snapshot.device_type.replace(/_/g, ' ') }}
          <span v-if="snapshot.metadata?.width && snapshot.metadata?.height">
            - {{ snapshot.metadata.width }}x{{ snapshot.metadata.height }}
          </span>
        </p>
      </div>
    </template>

    <action-message :on="added" message="Your comment has been added" />

    <div class="max-w-10xl mx-auto pt-6 pb-10 px-2 sm:px-6">
      <div class="grid grid-cols-12 gap-6">
        <!-- Preview and Annotations Column -->
        <div class="col-span-12 lg:col-span-8">
          <!-- Preview Section -->
          <div class="bg-white border shadow sm:rounded-lg">
            <div class="p-4 border-b bg-light-grey sm:rounded-t-lg">
              <h3 class="text-lg leading-7 font-medium text-vice-teal-700">Snapshot Preview</h3>
            </div>
            <div class="p-6 bg-white rounded-xl">
              <div class="relative">
                <img
                  :key="snapshot.id"
                  :src="snapshot.file_url"
                  :alt="`${snapshot.device_type} snapshot`"
                  @load="previewLoaded = true"
                  class="w-full h-auto ring-1 ring-vice-teal-700 rounded-lg shadow-lg"
                  :class="{ 'opacity-0': !previewLoaded }"
                />
                <ViceLoader
                  v-if="!previewLoaded"
                  class="absolute inset-0 flex items-center justify-center z-20"
                  colorClass="text-vice-teal-700"
                  size="h-9 w-9"
                />
                <ArrowsExpandIcon
                  class="h-7 w-7 right-1 top-1 absolute text-white cursor-pointer z-30"
                  v-if="previewLoaded && !snapshotPreviewOpen"
                  @click="snapshotPreviewOpen = true"
                />
                <!-- Full Screen Preview Modal -->
                <TransitionRoot as="template" :show="snapshotPreviewOpen">
                  <Dialog
                    as="div"
                    class="relative zoom-80 z-50"
                    @close="snapshotPreviewOpen = false"
                  >
                    <TransitionChild
                      as="template"
                      enter="ease-out duration-300"
                      enter-from="opacity-0"
                      enter-to="opacity-100"
                      leave="ease-in duration-200"
                      leave-from="opacity-100"
                      leave-to="opacity-0"
                    >
                      <div class="fixed inset-0 bg-black bg-opacity-70 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 overflow-y-auto">
                      <div
                        class="
                          flex
                          min-h-full
                          items-center
                          md:items-end
                          justify-center
                          p-4
                          text-center
                          sm:items-center
                          sm:p-0
                        "
                      >
                        <TransitionChild
                          as="template"
                          enter="ease-out duration-300"
                          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                          enter-to="opacity-100 translate-y-0 sm:scale-100"
                          leave="ease-in duration-200"
                          leave-from="opacity-100 translate-y-0 sm:scale-100"
                          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                          <DialogPanel
                            class="
                              relative
                              transform
                              overflow-hidden
                              rounded-lg
                              bg-white
                              px-4
                              pt-5
                              pb-4
                              text-left
                              shadow-xl
                              transition-all
                              max-h-screen
                              sm:my-8
                              sm:max-w-7xl
                              sm:p-6
                              min-w-20 min-h-20
                            "
                          >
                            <img
                              class="w-full max-h-screen rounded relative block object-contain"
                              :src="snapshot.file_url"
                              alt=""
                            />
                          </DialogPanel>
                        </TransitionChild>
                      </div>
                    </div>
                  </Dialog>
                </TransitionRoot>
              </div>
            </div>
          </div>

          <!-- Annotations Section -->
          <div class="shadow mt-6 border sm:rounded-lg">
            <div class="p-4 border-b bg-light-grey sm:rounded-t-lg">
              <h3 class="text-lg leading-7 font-medium text-vice-teal-700">Description</h3>
            </div>
            <div class="px-3 py-3 bg-white rounded-b-lg">
              <div
                v-if="snapshot.annotations"
                class="prose max-w-none text-sm"
                v-html="snapshot.annotations"
              ></div>
              <div v-else class="text-gray-500 text-sm italic">No description provided</div>
            </div>
          </div>
        </div>

        <!-- Comments Column -->
        <div class="col-span-12 lg:col-span-4">
          <div class="bg-white border shadow sm:rounded-lg sticky top-6">
            <div class="p-4 border-b bg-light-grey sm:rounded-t-lg">
              <h3 class="text-lg leading-7 font-medium text-vice-teal-700">Comments</h3>
            </div>
            <div class="bg-white sm:rounded-b-lg">
              <!-- Comments List -->
              <div class="px-3 py-3 space-y-4">
                <div
                  v-for="comment in comments"
                  :key="comment.id"
                  class="border rounded-md border-gray-200 p-3 transition-all duration-500"
                >
                  <div class="flex justify-between pb-1">
                    <div class="flex items-center">
                      <div class="flex items-center">
                        <img
                          v-if="comment.commenter.profile_photo_url"
                          :src="comment.commenter.profile_photo_url"
                          :alt="comment.commenter.name"
                          class="h-8 w-8 rounded-full object-cover"
                        />
                        <div
                          v-else
                          class="
                            h-8
                            w-8
                            rounded-full
                            flex
                            items-center
                            justify-center
                            text-white
                            font-medium
                          "
                          :class="
                            comment.commenter.role === 'clinician'
                              ? 'bg-vice-medium-blue'
                              : 'bg-vice-teal-500'
                          "
                        >
                          {{ getInitials(comment.commenter.name) }}
                        </div>
                      </div>
                      <div class="flex ml-2 gap-2">
                        <p class="flex text-sm font-medium text-gray-900">
                          {{ comment.commenter.name }}
                        </p>
                        <span
                          v-if="comment.commenter.id === snapshot.clinician.id"
                          class="text-xxs content-center text-gray-600 bg-gray-150 px-1 rounded-md"
                        >
                          Author
                        </span>
                        <span
                          v-else-if="comment.commenter.role === 'clinician'"
                          class="
                            text-xxs
                            content-center
                            bg-vice-light-blue
                            text-gray-600
                            px-1
                            rounded-md
                          "
                        >
                          Clinician
                        </span>
                        <span
                          v-else
                          class="
                            text-xxs
                            content-center
                            bg-vice-teal-100
                            text-gray-600
                            px-1
                            rounded-md
                          "
                        >
                          Student
                        </span>
                      </div>
                    </div>

                    <div class="flex gap-2 items-center">
                      <span class="flex text-xxs text-gray-400">
                        {{ formatDate(comment.created_at) }}
                      </span>
                      <button
                        v-if="canDeleteComment(comment)"
                        @click="deleteComment(comment.id)"
                        class="
                          text-red-500
                          hover:text-red-700
                          transition-colors
                          duration-200
                          p-1
                          rounded
                          hover:bg-red-50
                        "
                        title="Delete comment"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div class="flex items-start space-x-3">
                    <div class="flex-1 mt-1 min-w-0">
                      <p
                        class="
                          mt-1
                          ml-1
                          text-sm text-gray-700
                          hover:text-gray-900
                          whitespace-pre-wrap
                        "
                      >
                        {{ comment.comment }}
                      </p>
                    </div>
                  </div>
                </div>
                <div v-if="comments.length === 0" class="text-gray-500 text-sm text-center py-2">
                  No comments yet.
                </div>
              </div>

              <!-- Add Comment Form -->
              <div class="border-t border-gray-200 p-3">
                <form @submit.prevent="submitComment">
                  <textarea
                    v-model="newComment"
                    rows="3"
                    class="
                      block
                      w-full
                      rounded-md
                      border-gray-300
                      shadow-sm
                      focus:border-vice-teal-500
                      focus:ring-vice-teal-500
                      text-sm
                    "
                    placeholder="Add a comment..."
                    :disabled="isSubmitting"
                  ></textarea>
                  <div class="mt-3 flex justify-end">
                    <button
                      type="submit"
                      :disabled="!newComment.trim() || isSubmitting"
                      class="
                        inline-flex
                        items-center
                        px-4
                        py-2
                        border border-transparent
                        rounded-md
                        shadow-sm
                        text-sm
                        font-medium
                        text-white
                        bg-vice-teal-500
                        hover:bg-vice-teal-600
                        focus:outline-none
                        focus:ring-2 focus:ring-offset-2 focus:ring-vice-teal-500
                        disabled:opacity-50
                        disabled:cursor-not-allowed
                      "
                    >
                      <ViceLoader v-if="isSubmitting" class="mr-2 h-4 w-4" />
                      <span>{{ isSubmitting ? 'Posting...' : 'Post Comment' }}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </clinician-app-layout>
</template>

<script>
import { ref } from 'vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ArrowsExpandIcon } from '@heroicons/vue/outline';
import { ChevronLeftIcon } from '@heroicons/vue/solid';
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import ClinicianAppLayout from '@/Layouts/ClinicianAppLayout.vue';
import ViceLoader from '@/Components/Loaders/ViceLoader.vue';
import ActionMessage from '@/Jetstream/ActionMessage.vue';

dayjs.extend(relativeTime);

export default {
  name: 'ViewSharedSnapshot',
  components: {
    ActionMessage,
    ClinicianAppLayout,
    ViceLoader,
    ChevronLeftIcon,
    ArrowsExpandIcon,
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
  },
  props: {
    snapshot: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const added = ref(false);
    const newComment = ref('');
    const isSubmitting = ref(false);
    const previewLoaded = ref(false);
    const snapshotPreviewOpen = ref(false);
    const comments = ref(props.snapshot.comments || []);

    return {
      added,
      comments,
      newComment,
      isSubmitting,
      previewLoaded,
      snapshotPreviewOpen,
    };
  },
  methods: {
    getInitials(name) {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },

    formatDate(date) {
      return dayjs(date).fromNow();
    },

    async submitComment() {
      if (!this.newComment.trim()) return;

      this.isSubmitting = true;

      try {
        const response = await axios.post(
          route('clinician.shared-content.comments.store', {
            type: 'snapshot',
            id: this.snapshot.id,
          }),
          { comment: this.newComment },
        );

        if (response.data.success) {
          this.comments.push(response.data.data);
          this.newComment = '';
          this.added = true;
        } else {
          console.error('Failed to add comment:', response.data.message);
        }
      } catch (error) {
        console.error('Error adding comment:', error);
      } finally {
        this.isSubmitting = false;
        this.added = true;
      }
    },
    canDeleteComment(comment) {
      return comment.commenter_id === this.$page.props.user.id;
    },

    async deleteComment(commentId) {
      try {
        const response = await axios.delete(
          route('clinician.shared-content.comments.destroy', {
            commentId,
          }),
        );

        if (response.data.success) {
          const index = this.comments.findIndex((c) => c.id === commentId);
          if (index !== -1) {
            this.comments.splice(index, 1);
          }
        } else {
          console.error('Failed to delete comment:', response.data.message);
        }
      } catch (error) {
        console.error('Error deleting comment:', error);
      }
    },
  },
};
</script>

<style scoped>
.prose {
  color: #374151;
}

.prose :deep(p) {
  margin-bottom: 0.5rem;
}

.prose :deep(ul),
.prose :deep(ol) {
  margin-left: 1.5rem;
  margin-bottom: 0.5rem;
}

.prose :deep(li) {
  margin-bottom: 0.25rem;
}
</style>
